import React, { useState, useEffect, useRef } from 'react';
import { useStock } from '../hooks/useStock';
import { getSiteConfig } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import LivePurchasePopup from '../components/LivePurchasePopup';
import FAQ from '../components/FAQ';

const PINK = '#e8a4b4';
const PINK_DIM = 'rgba(232,164,180,0.15)';
const PINK_BORDER = 'rgba(232,164,180,0.25)';

const ACHIEVEMENT_META = [
  { icon: '👋', label: 'First Visit', desc: 'Created an account', unlocked: true },
  { icon: '🎉', label: 'First Purchase', desc: 'Completed your first order', unlocked: false },
  { icon: '🔥', label: '10 Orders', desc: 'Placed 10 completed orders', unlocked: false },
  { icon: '💎', label: 'Big Spender', desc: 'Spent $100 or more', unlocked: false },
];

const TIER_LOOP = [
  { label: 'Bronze Member', color: '#cd7f32', bg: 'rgba(205,127,50,0.15)', border: 'rgba(205,127,50,0.35)' },
  { label: 'Silver Member', color: '#c0c0c0', bg: 'rgba(192,192,192,0.12)', border: 'rgba(192,192,192,0.3)' },
  { label: 'Gold Member', color: '#ffd700', bg: 'rgba(255,215,0,0.12)', border: 'rgba(255,215,0,0.3)' },
  { label: 'Official Reseller', color: '#a855f7', bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.35)' },
];

const AnimatedDashboard = () => {
  const [tierIdx, setTierIdx] = useState(0);
  const [spent, setSpent] = useState(0);
  const [achCount, setAchCount] = useState(1);
  const [unlockedAch, setUnlockedAch] = useState([true, false, false, false]);
  const [orderCount, setOrderCount] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setTierIdx(i => {
        const next = (i + 1) % TIER_LOOP.length;
        if (next === 0) {
          setSpent(0); setAchCount(1); setOrderCount(0);
          setUnlockedAch([true, false, false, false]);
        } else if (next === 1) {
          setSpent(18.50); setAchCount(2); setOrderCount(3);
          setUnlockedAch([true, true, false, false]);
        } else if (next === 2) {
          setSpent(74.20); setAchCount(3); setOrderCount(10);
          setUnlockedAch([true, true, true, false]);
        } else if (next === 3) {
          setSpent(142.80); setAchCount(4); setOrderCount(24);
          setUnlockedAch([true, true, true, true]);
        }
        return next;
      });
    }, 2600);
    return () => clearInterval(t);
  }, []);

  const tier = TIER_LOOP[tierIdx];
  const progress = tierIdx === 0 ? 0 : tierIdx === 1 ? 74 : tierIdx === 2 ? 100 : 100;

  return (
    <div
      style={{
        background: '#0f1012',
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.07)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '480px',
        boxShadow: `0 20px 60px rgba(0,0,0,0.75), 0 0 50px ${PINK_DIM}`,
        transform: 'perspective(900px) rotateY(-5deg) rotateX(3deg)',
        transition: 'transform 0.35s ease',
        userSelect: 'none',
        fontSize: 13,
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'perspective(900px) rotateY(-1deg) rotateX(0.5deg)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'perspective(900px) rotateY(-5deg) rotateX(3deg)'}
    >
      {/* Browser chrome */}
      <div style={{ background: '#18191c', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
        <div style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.28)' }}>fastboost.cc/dashboard</div>
      </div>

      {/* User profile card */}
      <div style={{ background: '#13141a', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          {/* Avatar */}
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: `2px solid ${tier.color}`, background: 'rgba(219,39,119,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, transition: 'border-color 0.5s ease', flexShrink: 0 }}>👤</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>Customer</span>
              <span style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#22c55e', fontSize: 9, padding: '2px 7px', borderRadius: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
                $0.00
              </span>
            </div>
            <span style={{ background: tier.bg, border: `1px solid ${tier.border}`, color: tier.color, fontSize: 9, padding: '2px 8px', borderRadius: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, display: 'inline-block', marginTop: 4, transition: 'all 0.5s ease' }}>
              {tier.label}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[
            { val: orderCount, label: 'Orders' },
            { val: `$${spent.toFixed(2)}`, label: 'Total Spent' },
            { val: achCount, label: 'Achievements' },
          ].map(({ val, label }) => (
            <div key={label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums', transition: 'all 0.4s ease' }}>{val}</div>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Loyalty progress */}
      <div style={{ background: '#13141a', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>Loyalty Progress</div>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: tier.color, borderRadius: 4, transition: 'width 0.8s ease, background 0.5s ease' }} />
        </div>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 5 }}>
          ${spent.toFixed(2)} / {tierIdx === 0 ? '$25 to reach Silver' : tierIdx === 1 ? '$100 to reach Gold' : 'Highest tier reached'}
        </div>
      </div>

      {/* Achievements */}
      <div style={{ background: '#13141a', padding: '10px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>Achievements</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {ACHIEVEMENT_META.map((a, i) => {
            const unlocked = unlockedAch[i];
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 7, background: unlocked ? 'rgba(34,197,94,0.07)' : 'rgba(255,255,255,0.02)', border: `1px solid ${unlocked ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)'}`, transition: 'all 0.4s ease', opacity: unlocked ? 1 : 0.45 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13 }}>{a.icon}</span>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: unlocked ? '#fff' : 'rgba(255,255,255,0.5)' }}>{a.label}</div>
                    <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.28)' }}>{a.desc}</div>
                  </div>
                </div>
                {unlocked && <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const { stock, loading, error, lastUpdated } = useStock(3000);
  const [siteConfig, setSiteConfig] = useState(null);
  const { t } = useLanguage();
  const productsRef = useRef(null);
  const howItWorksRef = useRef(null);

  useEffect(() => {
    getSiteConfig().then(c => setSiteConfig(c)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'Fast Boost — Premium Discord Server Boosts | Instant Delivery';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Buy premium Discord server boosts with instant delivery. Secure crypto & card payments. Unlock Level 3 perks. Cheapest boosts guaranteed.');
  }, []);

  const formatTime = (date) => date ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) : '';

  const scrollToProducts = () => productsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToHowItWorks = () => howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <main className="main-content">
      {siteConfig?.livePurchasePopup?.enabled && <LivePurchasePopup />}

      {/* HERO — two-column: text left, dashboard right */}
      <section className="hero" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem', alignItems: 'center', padding: '3rem 0 2rem', textAlign: 'left' }}>
        <div>
          {/* Instant Delivery pill */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: PINK_DIM, border: `1px solid ${PINK_BORDER}`, borderRadius: 20, padding: '5px 13px', marginBottom: '1.2rem', fontSize: 11, fontWeight: 700, color: PINK, textTransform: 'uppercase', letterSpacing: 1 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: PINK, display: 'inline-block' }} />
            Instant Delivery
          </div>

          <h1 className="hero-title" style={{ fontSize: 'clamp(2rem, 3.5vw, 3.5rem)', lineHeight: 1.1, marginBottom: '1rem' }}>
            {t('hero.title')} <span style={{ background: 'linear-gradient(to right, #e8a4b4, #d4838f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t('hero.titleHighlight')}</span>
          </h1>

          <p className="hero-subtitle" style={{ marginBottom: '1.8rem', fontSize: '1rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 }}>{t('hero.subtitle')}</p>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: '0.9rem', marginBottom: '2.2rem', flexWrap: 'wrap' }}>
            <button
              onClick={scrollToProducts}
              style={{ background: PINK, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 26px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.2s', boxShadow: `0 4px 18px ${PINK_DIM}` }}
              onMouseEnter={e => { e.currentTarget.style.background = '#d4838f'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = PINK; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Get Started →
            </button>
            <button
              onClick={scrollToHowItWorks}
              style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '12px 26px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.background = 'transparent'; }}
            >
              How it works
            </button>
          </div>

          {/* Stats row — same style as original */}
          <div className="stats-row" style={{ justifyContent: 'flex-start', marginBottom: 0, padding: 0 }}>
            <div className="stat-item"><div className="stat-value">10k+</div><div className="stat-label">Boosts</div></div>
            <div className="stat-item"><div className="stat-value">2k+</div><div className="stat-label">Customers</div></div>
            <div className="stat-item"><div className="stat-value">24/7</div><div className="stat-label">Support</div></div>
          </div>
        </div>

        {/* 3D Dashboard */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <AnimatedDashboard />
        </div>
      </section>

      <h2 className="section-title" ref={productsRef}>{t('products.choosePackage')}</h2>
      <p className="section-subtitle">{t('products.selectDuration')}</p>

      {lastUpdated && (
        <div className="stock-updated">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          {t('products.liveStock')}: {formatTime(lastUpdated)}
        </div>
      )}

      {loading && (<div style={{ textAlign: 'center', padding: '3rem' }}><span className="loading-spinner" style={{ width: '40px', height: '40px' }}></span></div>)}
      {error && (<div style={{ textAlign: 'center', padding: '2rem', color: 'var(--red-primary)' }}>Failed to load products. Please refresh the page.</div>)}

      {!loading && !error && (
        <div className="products-grid">
          {stock.map((product, index) => (
            <ProductCard key={product.id || index} product={product} isPopular={index === 0 && product.stock > 0} siteConfig={siteConfig} />
          ))}
        </div>
      )}

      <section className="features-section" ref={howItWorksRef}>
        <h2 className="features-title">{t('process.title')}</h2>
        <p className="features-subtitle">{t('process.subtitle')}</p>
        <div className="process-grid">
          <div className="process-card process-card-1"><span className="process-step-badge step-blue">{t('process.step1')}</span><h3>{t('process.step1Title')}</h3><p>{t('process.step1Desc')}</p></div>
          <div className="process-card process-card-2"><span className="process-step-badge step-purple">{t('process.step2')}</span><h3>{t('process.step2Title')}</h3><p>{t('process.step2Desc')}</p></div>
          <div className="process-card process-card-3"><span className="process-step-badge step-green">{t('process.step3')}</span><h3>{t('process.step3Title')}</h3><p>{t('process.step3Desc')}</p></div>
          <div className="process-card process-card-4"><span className="process-step-badge step-amber">{t('process.step4')}</span><h3>{t('process.step4Title')}</h3><p>{t('process.step4Desc')}</p></div>
        </div>
      </section>

      <section className="features-section">
        <h2 className="features-title">{t('why.title')}</h2>
        <p className="features-subtitle">{t('why.subtitle')}</p>
        <div className="features-grid-expanded">
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg></div><h3>{t('why.payment')}</h3><p>{t('why.paymentDesc')}</p></div>
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg></div><h3>{t('why.pricing')}</h3><p>{t('why.pricingDesc')}</p></div>
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(232, 164, 180, 0.1)', color: '#e8a4b4' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div><h3>{t('why.safe')}</h3><p>{t('why.safeDesc')}</p></div>
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div><h3>{t('why.support')}</h3><p>{t('why.supportDesc')}</p></div>
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div><h3>{t('why.trust')}</h3><p>{t('why.trustDesc')}</p></div>
          <div className="feature-card-v2"><div className="feature-icon-v2" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg></div><h3>{t('why.instant')}</h3><p>{t('why.instantDesc')}</p></div>
        </div>
      </section>

      {siteConfig?.faq && <FAQ items={siteConfig.faq} />}

      <section className="reviews-section" style={{ padding: 60 }}>
        <h2 className="features-title">{t('reviews.title')}</h2>
        <p className="features-subtitle">{t('reviews.subtitle')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', maxWidth: '800px', margin: '0 auto' }}>
          <a href="https://myvouch.es/fast-boost" target="_blank" rel="noopener noreferrer" className="review-link review-link-blue">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div className="review-icon-box review-icon-blue"><img src="https://myvouch.es/favicon.ico" alt="MyVouches" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:1.2rem;font-weight:700;color:#60a5fa">MV</span>'; }} /></div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem' }}>MyVouch</span>
                <span style={{ color: 'rgba(147, 197, 253, 0.8)', fontSize: '0.875rem' }}>myvouch.es</span>
                <div style={{ display: 'flex', gap: '0.125rem', marginTop: '0.25rem' }}>{[...Array(5)].map((_, i) => (<svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#ec4899"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>))}</div>
              </div>
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
          <a href="https://www.trustpilot.com/review/fastboost.cc" target="_blank" rel="noopener noreferrer" className="review-link review-link-green">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div className="review-icon-box review-icon-green"><svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}><path d="M50 10 L61 41 L94 41 L68 59 L79 90 L50 72 L21 90 L32 59 L6 41 L39 41 Z" fill="#00B67A"/></svg></div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.125rem' }}>Trustpilot</span>
                <span style={{ color: 'rgba(134, 239, 172, 0.8)', fontSize: '0.875rem' }}>trustpilot.com</span>
                <div style={{ display: 'flex', gap: '0.125rem', marginTop: '0.25rem' }}>{[...Array(5)].map((_, i) => (<svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#ec4899"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>))}</div>
              </div>
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </section>
    </main>
  );
};

export default Home;